/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useMemo} from 'react';
import {useSelector} from 'react-redux';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import {translate} from '@ion/core/src/locale/locale';
import {Link} from '@ion/core/src/types/entities';
import Breadcrumb from '@ion/dls/src/components/breadcrumbs/Breadcrumbs';
import {defineBreadcrumbs} from '@ion/dls/src/components/breadcrumbs/BreadcrumbUtils';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import styles, {getClasses} from '@ion/dls/src/editors/editorStyles';
import {
  useAnimatedMount
} from '@ion/dls/src/hooks/useAnimatedMount';
import {
  useExtendStyles
} from '@ion/dls/src/hooks/useExtendStyles';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {DLSTheme, ViewSubGroupsPosition} from '@ion/dls/src/types/types';
import {PageTitleSize} from '../../pages/admin/AdminContainer';
import Devices from '../../pages/devices/Devices';
import {
  devicesAreTotalRowsSelectedSelector,
  devicesFiltersSelector,
  devicesGridSearchSelector,
  devicesIncludeSubAccountsSelector,
  devicesSearchSelector,
  devicesSelectedSelector,
  devicesTotalRowsCountSelector
} from '../../pages/devices/state-selectors';

const styleOverrides = (theme: DLSTheme) => ({
  formPaper : {
    width                          : '75%',
    [theme.breakpoints.down('sm')] : {
      maxWidth : '100%'
    }
  },
  closeButton : {
    [theme.breakpoints.down('sm')] : {
      left : '10px'
    }
  },
  paddingNone : {
    padding : 0
  },
  label : {
    ...dlsTypography('h1'),
    margin        : '30px auto',
    fontSize      : '20px',
    textTransform : 'uppercase'
  },
  chevron : {
    fill : dlsColor('grey3')
  },
  activeCrumb : {
    color : dlsColor('grey1')
  },
  inactiveCrumb : {
    color     : dlsColor('grey3'),
    '&:hover' : {
      cursor : 'pointer'
    }
  },
  deviceDetailGridBody : {
    overflowY                      : 'auto',
    [theme.breakpoints.down('sm')] : {
      padding : '0 20px'
    }
  }
});

export interface JobSelectDevicesDialogProps {
  account: Link;
  breadcrumbBackLabel?: string;
  onBreadcrumbBackClick?: Function;
  onSelectDevicesDone?: Function;
  onCloseDialog?: Function;
}

const excludedColumns = [
  'deviceId', 'enabledServicesUI', 'assetName', 'iOnTag', 'statusUI', 'airId', 'appIdUI', 'esn',
  'serialNumber', 'iccid', 'imei', 'imei2', 'imsi', 'isdn', 'mdnUI', 'meid', 'deviceConfigurationUI',
  'firmwareVersionUI', 'configVersionUI', 'category', 'architecture', 'groups',
  'lastReportedTime', 'lastAvlCommUI', 'dmDevice', 'idReportTime', 'vbusStatusUI'
];
const filterableColumns = [];

/**
 * The JobSelectDevicesDialog component contains a breadcrumb, modular search, and grid for
 * selecting devices to be used in job creation.
 */
export const JobSelectDevicesDialog = ({
  account,
  breadcrumbBackLabel,
  onBreadcrumbBackClick,
  onSelectDevicesDone,
  onCloseDialog
}: JobSelectDevicesDialogProps) => {
  const editorStyles = useExtendStyles(styles, styleOverrides);
  const classes      = editorStyles();
  const {
    isVisible,
    onRemove,
    timeout
  } = useAnimatedMount();
  const selectedDevices      = useSelector(devicesSelectedSelector);
  const includeSubAccounts   = useSelector(devicesIncludeSubAccountsSelector);
  const areTotalRowsSelected = useSelector(devicesAreTotalRowsSelectedSelector);
  const totalRowsCount       = useSelector(devicesTotalRowsCountSelector);
  const search               = useSelector(devicesSearchSelector);
  const gridSearch           = useSelector(devicesGridSearchSelector);
  const filters              = useSelector(devicesFiltersSelector);

  const crumbData = useMemo(() => defineBreadcrumbs({
    crumbs : [
      {
        label   : breadcrumbBackLabel,
        click   : true,
        onClick : onBreadcrumbBackClick
      },
      {
        label : translate('common:editors.device.placeholder'),
        click : false
      }
    ],
    classes : {
      activeCrumb   : classes.activeCrumb,
      inactiveCrumb : classes.inactiveCrumb
    }
  }), [breadcrumbBackLabel, onBreadcrumbBackClick]);

  const onClose = useCallback(function() {
    onRemove(onCloseDialog);
  }, []);

  const onDone = function() {
    onClose();
    onSelectDevicesDone({
      selectedDevices,
      includeSubAccounts,
      areTotalRowsSelected,
      totalRowsCount,
      search,
      gridSearch,
      filters
    });
  };

  return (
    <Dialog disableEnforceFocus open
      transitionDuration={timeout}
      classes={getClasses(classes)}>
      <DialogTitle classes={{
        root : classes.formDialogTitle
      }}>
        <Breadcrumb icon={<ChevronLeft className={classes.chevron} />}
          height={'30px'}
          iconClick={onBreadcrumbBackClick}>
          {crumbData}
        </Breadcrumb>
      </DialogTitle>
      <div className={classes.deviceDetailGridBody}>
        <div className={classes.formWrapper}>
          <DialogContent className={classes.paddingNone}>
            <Devices isCtcAdmin
              isMultiEditEnabled
              showDefaultDefinitions
              enableSelectAll
              pageTitleSize={PageTitleSize.SMALL}
              globalGroup={account}
              Features={{}}
              excludedColumns={excludedColumns}
              filterableColumns={filterableColumns}
              rowMenusEnabled={false}
              viewSubGroupsPosition={ViewSubGroupsPosition.GRID_CTAS}
              showAdd={false}
              isSortByParams
              isGridInsideADialog
              disableImport
              includeModularSearch
              showFullWidthSearch
              omitAdminCTAs
              omitGridHeaderCTAs />
          </DialogContent>
        </div>
      </div>
      <DialogActions className={classes.formActions}>
        <TertiaryButton onClick={onClose}>
          {translate('common:editors.shared.cancel')}
        </TertiaryButton>
        <div className={classes.primaryButton}>
          <PrimaryButton fullWidth onClick={onDone}>
            {translate('common:editors.shared.done')}
          </PrimaryButton>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default JobSelectDevicesDialog;
